import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  searchBounds, 
  defaultCenter 
} from './constants';
import { 
  logDebug, 
  extractLocationName, 
  filterNearbyPlaces, 
  filterGeocoderResults 
} from './utils';

type Location = {
  lat: number;
  lng: number;
};

type AddressData = {
  address: string;
  locationName: string;
  alternateAddresses: {name: string, address: string}[];
};

// Custom debounce hook
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Hook for user geolocation
export function useUserLocation() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userLocationRef = useRef<Location | null>(null);
  const [locationPermissionChecked, setLocationPermissionChecked] = useState(false);
  
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return Promise.reject('Geolocation is not supported');
    }
    
    setIsLoading(true);
    setError(null);
    
    return new Promise<Location>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        position => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          logDebug('Current location detected', pos);
          userLocationRef.current = pos;
          setIsLoading(false);
          resolve(pos);
        },
        error => {
          logDebug('Error getting current location', error);
          setIsLoading(false);
          setError(error.message);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    });
  }, []);
  
  // Removed automatic location request on mount to prevent permission prompts
  // Location should only be requested when user explicitly needs it
  // useEffect(() => {
  //   if (!locationPermissionChecked) {
  //     setLocationPermissionChecked(true);
  //     getUserLocation().catch(() => {
  //       // Silently handle initial geolocation error
  //     });
  //   }
  // }, [locationPermissionChecked, getUserLocation]);
  
  return {
    userLocationRef,
    isLoading,
    error,
    getUserLocation,
    locationPermissionChecked
  };
}

// Helper to get preferred language based on browser settings
const getPreferredLanguage = (): string => {
  // Get browser language
  const browserLang = navigator.language || (navigator as any).userLanguage || '';
  
  // Check if Thai is preferred
  if (browserLang.toLowerCase().includes('th')) {
    return 'th';
  }
  
  // Default to English
  return 'en';
};

// Hook for geocoding functionality
export function useGeocoding() {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const geocodeTimeoutRef = useRef<number | null>(null);
  
  const geocodePosition = useCallback((position: Location): Promise<AddressData> => {
    setIsGeocoding(true);
    
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps) {
        setIsGeocoding(false);
        reject('Google Maps API not loaded');
        return;
      }
      
      // Cancel any previous geocoding requests
      if (geocodeTimeoutRef.current !== null) {
        clearTimeout(geocodeTimeoutRef.current);
      }
      
      const timeoutId = window.setTimeout(() => {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ 
          location: position,
          language: getPreferredLanguage()
        }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const address = results[0].formatted_address;
            const locationName = extractLocationName(results[0]);
            
            // Default empty alternates, to be filled by the caller if needed
            const result: AddressData = {
              address,
              locationName,
              alternateAddresses: []
            };
            
            if (results.length > 1) {
              result.alternateAddresses = filterGeocoderResults(results);
            }
            
            resolve(result);
          } else {
            reject({
              status,
              message: 'Geocoding failed'
            });
          }
          
          setIsGeocoding(false);
          geocodeTimeoutRef.current = null;
        });
      }, 300);
      
      geocodeTimeoutRef.current = timeoutId as unknown as number;
    });
  }, []);
  
  const searchAddress = useCallback((query: string): Promise<{position: Location, address: string, locationName: string}> => {
    setIsGeocoding(true);
    
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps) {
        setIsGeocoding(false);
        reject('Google Maps API not loaded');
        return;
      }
      
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ 
        address: query,
        bounds: new google.maps.LatLngBounds(
          new google.maps.LatLng(searchBounds.south, searchBounds.west),
          new google.maps.LatLng(searchBounds.north, searchBounds.east)
        ),
        componentRestrictions: { country: 'th' },
        language: getPreferredLanguage()
      }, (results, status) => {
        setIsGeocoding(false);
        
        if (status === "OK" && results && results[0]) {
          const place = results[0];
          if (place.geometry && place.geometry.location) {
            const position = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            
            const address = place.formatted_address || query;
            let locationName = '';
            
            if (place.address_components) {
              const poiComponent = place.address_components.find(component => 
                component.types.includes('establishment') || 
                component.types.includes('point_of_interest')
              );
              
              if (poiComponent) {
                locationName = poiComponent.long_name;
              } else {
                const premiseComponent = place.address_components.find(component => 
                  component.types.includes('premise') || 
                  component.types.includes('subpremise')
                );
                
                if (premiseComponent) {
                  locationName = premiseComponent.long_name;
                } else {
                  locationName = place.formatted_address?.split(',')[0] || query;
                }
              }
            } else {
              locationName = query;
            }
            
            resolve({ position, address, locationName });
          } else {
            reject({
              status: 'GEOMETRY_ERROR',
              message: 'No geometry in result'
            });
          }
        } else {
          reject({
            status,
            message: 'Search geocoding failed'
          });
        }
      });
    });
  }, []);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (geocodeTimeoutRef.current !== null) {
        clearTimeout(geocodeTimeoutRef.current);
      }
    };
  }, []);
  
  return {
    isGeocoding,
    geocodePosition,
    searchAddress,
    geocodeTimeoutRef
  };
}

// Hook for nearby places functionality
export function useNearbyPlaces() {
  const [isLoading, setIsLoading] = useState(false);
  
  const findNearbyPlaces = useCallback((
    map: google.maps.Map, 
    position: Location,
    currentAddress?: string
  ): Promise<{name: string, address: string}[]> => {
    setIsLoading(true);
    
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setIsLoading(false);
        reject('Google Maps Places API not loaded');
        return;
      }
      
      const placesService = new google.maps.places.PlacesService(map);
      placesService.nearbySearch({
        location: position,
        type: 'establishment',
        rankBy: google.maps.places.RankBy.DISTANCE
      }, (places, status) => {
        setIsLoading(false);
        
        if (status === google.maps.places.PlacesServiceStatus.OK && places) {
          const filteredPlaces = filterNearbyPlaces(places, currentAddress);
          resolve(filteredPlaces);
        } else {
          resolve([]);
        }
      });
    });
  }, []);
  
  return {
    isLoading,
    findNearbyPlaces
  };
}

// Hook for handling map position and address state
export function useMapLocation() {
  const [markerPosition, setMarkerPosition] = useState<Location>(defaultCenter);
  const [address, setAddress] = useState('');
  const [locationName, setLocationName] = useState('');
  const [alternateAddresses, setAlternateAddresses] = useState<{name: string, address: string}[]>([]);
  const lastPositionRef = useRef<Location>(defaultCenter);
  
  const updatePosition = useCallback((position: Location) => {
    lastPositionRef.current = position;
    setMarkerPosition(position);
  }, []);
  
  return {
    markerPosition,
    address,
    locationName,
    alternateAddresses,
    lastPositionRef,
    setMarkerPosition,
    setAddress,
    setLocationName,
    setAlternateAddresses,
    updatePosition
  };
} 